import m from 'mithril'
import type { Restaurant, Suggestion } from '../../shared/types.ts'
import { api, query } from '../api.ts'
import { state } from '../state.ts'
import { ErrorNote } from '../ui.ts'
import { PinMap } from './PinMap.ts'

const DEBOUNCE_MS = 250
const MIN_CHARS = 3

// Module scope, so opening a restaurant and coming back keeps the search where it was.
const search = {
  text: '',
  suggestions: [] as Suggestion[],
  open: false,
  /** True from the first keystroke until that text's answer arrives (debounce + request). */
  waiting: false,
  highlighted: -1,
  error: '',
}

type Point = { lat: number; lon: number }

const NEAR_KEY = 'foodtracker.near'

const loadNear = (): Point | null => {
  try {
    return JSON.parse(localStorage.getItem(NEAR_KEY) ?? 'null')
  } catch {
    return null
  }
}

/** Where to rank results around: the device's location, else the last place picked. */
let near: Point | null = loadNear()
let asked = false
let fromDevice = false

const setNear = (point: Point) => {
  near = point
  try {
    localStorage.setItem(NEAR_KEY, JSON.stringify(point))
  } catch {}
}

const locateDevice = () => {
  if (asked || !navigator.geolocation) return
  asked = true
  navigator.geolocation.getCurrentPosition(
    ({ coords }) => {
      fromDevice = true
      setNear({ lat: coords.latitude, lon: coords.longitude })
    },
    () => {
      // Declined or unavailable: keep using the last picked place, if any.
    },
    { maximumAge: 10 * 60_000, timeout: 10_000 },
  )
}

let timer: ReturnType<typeof setTimeout> | undefined
// Answers can arrive out of order; only the newest request may update the list.
let latest = 0

const fetchSuggestions = async (text: string) => {
  const ticket = ++latest
  try {
    const results = await api<Suggestion[]>(
      `/api/restaurants/suggest${query({ q: text, lat: near?.lat, lon: near?.lon })}`,
    )
    if (ticket !== latest) return
    search.suggestions = results
    search.highlighted = -1
    search.error = ''
  } catch (err) {
    if (ticket === latest) search.error = (err as Error).message
  } finally {
    if (ticket === latest) {
      search.waiting = false
      m.redraw()
    }
  }
}

const onType = (text: string) => {
  search.text = text
  search.open = true
  clearTimeout(timer)
  if (text.trim().length < MIN_CHARS) {
    latest++
    search.suggestions = []
    search.waiting = false
    return
  }
  search.waiting = true
  timer = setTimeout(() => fetchSuggestions(text.trim()), DEBOUNCE_MS)
}

const pick = (suggestion: Suggestion) => {
  search.open = false
  search.text = suggestion.name
  if (!fromDevice) setNear({ lat: suggestion.lat, lon: suggestion.lon })
  m.route.set(`/r/${suggestion.ref}`)
}

const onKey = (event: KeyboardEvent) => {
  const count = search.suggestions.length
  if (event.key === 'ArrowDown' && count) {
    event.preventDefault()
    search.highlighted = (search.highlighted + 1) % count
  } else if (event.key === 'ArrowUp' && count) {
    event.preventDefault()
    search.highlighted = (search.highlighted - 1 + count) % count
  } else if (event.key === 'Enter') {
    event.preventDefault()
    const choice = search.suggestions[Math.max(0, search.highlighted)]
    if (choice) pick(choice)
  } else if (event.key === 'Escape') {
    search.open = false
  }
}

const glyph = (d: string) =>
  m(
    'svg.field-icon',
    {
      viewBox: '0 0 24 24',
      width: 18,
      height: 18,
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 2,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'aria-hidden': 'true',
    },
    m('path', { d }),
  )

const SEARCH_ICON = 'M10.5 3a7.5 7.5 0 1 1 0 15 7.5 7.5 0 0 1 0-15zM21 21l-5.4-5.4'
const PIN_ICON = 'M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21zM12 7.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4z'
const CLEAR_ICON = 'M6 6l12 12M18 6L6 18'

const Suggestions: m.Component = {
  view: () => {
    const typed = search.text.trim().length >= MIN_CHARS
    if (!search.open || !typed) return null
    if (!search.suggestions.length) {
      // Only once the answer for exactly this text is in; never while typing or waiting.
      return search.waiting ? null : m('div.suggestions', m('p.suggestion-empty', 'No matches'))
    }
    return m(
      'ul.suggestions',
      { role: 'listbox', id: 'suggestions' },
      search.suggestions.map((suggestion, index) =>
        m(
          'li.suggestion',
          {
            key: suggestion.ref,
            role: 'option',
            id: `suggestion-${index}`,
            'aria-selected': index === search.highlighted,
            class: index === search.highlighted ? 'highlighted' : '',
            // mousedown, not click: it fires before the input's blur closes the list.
            onmousedown: (event: Event) => {
              event.preventDefault()
              pick(suggestion)
            },
          },
          [
            glyph(PIN_ICON),
            m('div.suggestion-text', [
              m('div.suggestion-name', suggestion.name),
              suggestion.address ? m('div.suggestion-meta', suggestion.address) : null,
              m('div.suggestion-meta', [suggestion.city, suggestion.state].filter(Boolean).join(', ')),
            ]),
          ],
        ),
      ),
    )
  },
}

export const SearchView: m.ClosureComponent = () => {
  let adding = false
  let saving = false
  let addError = ''
  let locating = false
  const draft = { name: '', cuisine: '', address: '', city: '' }
  let pin: { lat: number; lon: number } | null = null

  const locate = async () => {
    locating = true
    addError = ''
    try {
      const q = [draft.address, draft.city].map((part) => part.trim()).filter(Boolean).join(', ')
      pin = await api<{ lat: number; lon: number }>(`/api/geocode${query({ q })}`)
    } catch (err) {
      addError = `${(err as Error).message}. Tap the map to place the pin yourself.`
    } finally {
      locating = false
      m.redraw()
    }
  }

  const add = async (event: Event) => {
    event.preventDefault()
    if (!pin) {
      addError = 'Find the address on the map first, or tap the map to place the pin.'
      return
    }
    saving = true
    addError = ''
    try {
      const place = await api<Restaurant>('/api/restaurants', { method: 'POST', body: { ...draft, ...pin } })
      m.route.set(`/r/${place.ref}`)
    } catch (err) {
      addError = (err as Error).message
    } finally {
      saving = false
      m.redraw()
    }
  }

  const openAdd = () => {
    adding = true
    draft.name = search.text.trim()
    requestAnimationFrame(() =>
      document.querySelector('.add-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    )
  }

  const field = (label: string, value: string, placeholder: string, set: (value: string) => void) =>
    m('label', [
      label,
      m('input', {
        value,
        placeholder,
        oninput: (event: Event) => set((event.target as HTMLInputElement).value),
      }),
    ])

  return {
    oninit: locateDevice,
    view: () => [
      m('div.search-box', [
        m('div.search-field', [
          glyph(SEARCH_ICON),
          m('input', {
            type: 'search',
            enterkeyhint: 'search',
            autocomplete: 'off',
            autocorrect: 'off',
            spellcheck: false,
            role: 'combobox',
            'aria-label': 'Search restaurants',
            'aria-expanded': search.open && search.suggestions.length > 0,
            'aria-controls': 'suggestions',
            'aria-activedescendant': search.highlighted >= 0 ? `suggestion-${search.highlighted}` : undefined,
            placeholder: 'Search restaurants',
            value: search.text,
            oninput: (event: Event) => onType((event.target as HTMLInputElement).value),
            onfocus: () => {
              search.open = true
            },
            onblur: () => {
              search.open = false
            },
            onkeydown: onKey,
          }),
          search.waiting ? m('span.field-spinner', { 'aria-hidden': 'true' }) : null,
          !search.waiting && search.text
            ? m(
                'button.field-clear',
                {
                  type: 'button',
                  'aria-label': 'Clear search',
                  onmousedown: (event: Event) => event.preventDefault(),
                  onclick: () => {
                    onType('')
                    ;(document.querySelector('.search-field input') as HTMLInputElement | null)?.focus()
                  },
                },
                glyph(CLEAR_ICON),
              )
            : null,
        ]),
        m(Suggestions),
      ]),

      search.error ? m(ErrorNote, { text: search.error }) : null,

      state.me && !adding
        ? m('button.link-button', { type: 'button', onclick: openAdd }, 'Can’t find it? Add a restaurant')
        : null,

      adding
        ? m('form.card.stack.add-form', { onsubmit: add }, [
            m('div.add-head', [
              m('h2', 'Add a restaurant'),
              m('button.link-button', { type: 'button', onclick: () => (adding = false) }, 'Cancel'),
            ]),
            field('Name', draft.name, 'Joe’s Pizza', (value) => {
              draft.name = value
            }),
            field('Cuisine', draft.cuisine, 'pizza', (value) => {
              draft.cuisine = value
            }),
            m('div.row', [
              field('Address', draft.address, '7 Carmine St', (value) => {
                draft.address = value
              }),
              field('City', draft.city, 'New York', (value) => {
                draft.city = value
              }),
            ]),
            m(
              'button.button.block',
              { type: 'button', onclick: locate, disabled: locating || !draft.address.trim() },
              locating ? 'Finding…' : 'Find on map',
            ),
            m(PinMap, {
              pin,
              fallback: near,
              onmove: (point) => {
                pin = point
              },
            }),
            addError ? m(ErrorNote, { text: addError }) : null,
            m(
              'button.button.primary.block',
              { type: 'submit', disabled: saving || !pin },
              saving ? 'Adding…' : 'Add restaurant',
            ),
          ])
        : null,
    ],
  }
}
